'use client'

import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { pushDataLayerEvent } from '@/components/site/page-tracking'

interface Tab {
  id: string
  label: string
  caption: string
}

const HASH = '#method-'

/**
 * The method's department tabs. One tab list (arrow keys, Home and End move
 * between tabs) over one panel: every department's figures and diagrams are
 * already in the panel's HTML (`data-dept`), and switching only changes
 * which are `hidden`. Each tab has its own hash (`#method-<id>`, also the
 * tab's id, so a link scrolls to the tabs), read on load and on hash
 * changes, and written when a visitor switches, so a link opens that tab.
 * A switch marks the panel `data-switched`, so the shown diagrams replay
 * their drawing (CSS, never under reduced motion), and fires
 * `method_tab_view` with the tab's id.
 */
export function MethodTabs({ label, tabs, children }: { label: string; tabs: Tab[]; children: ReactNode }) {
  const [active, setActive] = useState(tabs[0]?.id)
  const activeRef = useRef(active)
  const panelRef = useRef<HTMLDivElement>(null)
  const tabRefs = useRef(new Map<string, HTMLButtonElement>())

  const select = useCallback(
    (id: string, { writeHash = true, focus = false } = {}) => {
      if (!tabs.some((tab) => tab.id === id)) return
      if (focus) tabRefs.current.get(id)?.focus()
      if (id === activeRef.current) return
      activeRef.current = id
      setActive(id)
      panelRef.current?.setAttribute('data-switched', '')
      if (writeHash) window.history.replaceState(null, '', `${HASH}${id}`)
      pushDataLayerEvent('method_tab_view', { tab_name: id })
    },
    [tabs],
  )

  // Show the active department's figures and diagrams, hide the others.
  useEffect(() => {
    panelRef.current?.querySelectorAll<HTMLElement>('[data-dept]').forEach((element) => {
      element.hidden = element.dataset.dept !== active
    })
  }, [active])

  // A link to #method-<id> opens that tab, on load and when the hash changes.
  useEffect(() => {
    const fromHash = () => {
      if (window.location.hash.startsWith(HASH)) select(window.location.hash.slice(HASH.length), { writeHash: false })
    }
    fromHash()
    window.addEventListener('hashchange', fromHash)
    return () => window.removeEventListener('hashchange', fromHash)
  }, [select])

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const index = tabs.findIndex((tab) => tab.id === active)
    const next =
      event.key === 'ArrowRight'
        ? (index + 1) % tabs.length
        : event.key === 'ArrowLeft'
          ? (index - 1 + tabs.length) % tabs.length
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? tabs.length - 1
              : -1
    if (next < 0) return
    event.preventDefault()
    select(tabs[next].id, { focus: true })
  }

  return (
    <>
      <div className="de-tabs-bar">
        <div role="tablist" aria-label={label} className="de-tabs" onKeyDown={onKeyDown}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              ref={(element) => {
                if (element) tabRefs.current.set(tab.id, element)
              }}
              id={`method-${tab.id}`}
              type="button"
              role="tab"
              aria-selected={tab.id === active}
              aria-controls="method-panel"
              tabIndex={tab.id === active ? 0 : -1}
              className="de-tab"
              onClick={() => select(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        {tabs.map((tab) => (
          <p key={tab.id} className="de-tabs-caption" hidden={tab.id !== active}>
            {tab.caption}
          </p>
        ))}
      </div>
      <div ref={panelRef} id="method-panel" role="tabpanel" aria-labelledby={`method-${active}`} className="de-method-panel">
        {children}
      </div>
    </>
  )
}
